import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import { validateUrl } from '../utils/validation';
import '../styles/CommunityProfile.css';

interface ProfileData {
  id: string;
  userId: string;
  displayName: string | null;
  headline: string | null;
  bio: string | null;
  avatarUrl: string | null;
  linkedInUrl: string | null;
  portfolioUrl: string | null;
  location: string | null;
  isPublic: boolean;
  showEmail: boolean;
  skills: string[];
  lookingFor: string[];
  industries: string[];
  experienceLevel: string | null;
  viewCount: number;
}

const EXPERIENCE_LEVELS = [
  { value: 'junior', label: 'Junior (0–2 years)' },
  { value: 'mid', label: 'Mid-Level (3–5 years)' },
  { value: 'senior', label: 'Senior (6–10 years)' },
  { value: 'executive', label: 'Executive (10+ years)' },
];

const LOOKING_FOR_OPTIONS = [
  'Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship',
  'Mentorship', 'Collaboration', 'Networking', 'Advice',
];

const INDUSTRY_OPTIONS = [
  'Technology', 'Finance', 'Healthcare', 'Education', 'Marketing',
  'Design', 'Engineering', 'Legal', 'Consulting', 'Non-profit',
  'Retail', 'Manufacturing', 'Media', 'Government', 'Other',
];

const CommunityProfile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState('');

  // Form fields
  const [displayName, setDisplayName] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [location, setLocation] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [skills, setSkills] = useState<string[]>([]);
  const [lookingFor, setLookingFor] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [experienceLevel, setExperienceLevel] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/community/profile');
      const p = response.data.profile;
      setProfile(p);
      setDisplayName(p.displayName || '');
      setHeadline(p.headline || '');
      setBio(p.bio || '');
      setAvatarUrl(p.avatarUrl || '');
      setLinkedInUrl(p.linkedInUrl || '');
      setPortfolioUrl(p.portfolioUrl || '');
      setLocation(p.location || '');
      setIsPublic(p.isPublic);
      setShowEmail(p.showEmail);
      setSkills(p.skills || []);
      setLookingFor(p.lookingFor || []);
      setIndustries(p.industries || []);
      setExperienceLevel(p.experienceLevel || '');
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }
    if (displayName.trim().length > 80) {
      toast.error('Display name is too long (max 80 characters)');
      return;
    }
    if (bio.trim().length > 2000) {
      toast.error('Bio is too long (max 2,000 characters)');
      return;
    }
    for (const [label, val] of [['LinkedIn URL', linkedInUrl], ['Portfolio URL', portfolioUrl], ['Avatar URL', avatarUrl]] as const) {
      const check = validateUrl(val.trim());
      if (!check.valid) { toast.error(`${label}: ${check.message}`); return; }
    }

    try {
      setSaving(true);
      const response = await axiosInstance.put('/auth/community/profile', {
        displayName: displayName.trim(),
        headline: headline.trim() || null,
        bio: bio.trim() || null,
        avatarUrl: avatarUrl.trim() || null,
        linkedInUrl: linkedInUrl.trim() || null,
        portfolioUrl: portfolioUrl.trim() || null,
        location: location.trim() || null,
        isPublic,
        showEmail,
        skills,
        lookingFor,
        industries,
        experienceLevel: experienceLevel || null,
      });
      setProfile(response.data.profile);
      toast.success('Profile saved!');
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addSkill = () => {
    const skill = skillInput.trim();
    if (skill && !skills.includes(skill)) {
      setSkills([...skills, skill]);
    }
    setSkillInput('');
  };

  const removeSkill = (skill: string) => {
    setSkills(skills.filter(s => s !== skill));
  };

  const toggleLookingFor = (item: string) => {
    setLookingFor(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const toggleIndustry = (item: string) => {
    setIndustries(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  if (loading) {
    return (
      <div className="cp-container">
        <div className="cp-loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="cp-container">
      <div className="cp-header">
        <button className="cp-back-btn" onClick={() => navigate('/community')}>
          ← Back to Community
        </button>
        <h1>My Community Profile</h1>
        <p>Set up your profile to connect with other job seekers</p>
      </div>

      <form onSubmit={handleSave} className="cp-form">
        {/* Visibility Settings */}
        <div className="cp-section cp-visibility">
          <h2>Visibility</h2>
          <div className="cp-toggle-row">
            <div className="cp-toggle-label">
              <span>Public Profile</span>
              <small>{isPublic ? 'Visible to other members' : 'Hidden from directory'}</small>
            </div>
            <label className="cp-toggle">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={e => setIsPublic(e.target.checked)}
              />
              <span className="cp-toggle-slider" />
            </label>
          </div>
          <div className="cp-toggle-row">
            <div className="cp-toggle-label">
              <span>Show email on public profile</span>
              <small>Your email will be visible to community members</small>
            </div>
            <label className="cp-toggle">
              <input
                type="checkbox"
                checked={showEmail}
                onChange={e => setShowEmail(e.target.checked)}
              />
              <span className="cp-toggle-slider" />
            </label>
          </div>
          {profile && (
            <p className="cp-views">👁 {profile.viewCount} profile views</p>
          )}
        </div>

        {/* Basic Info */}
        <div className="cp-section">
          <h2>Basic Information</h2>
          <div className="cp-field">
            <label>Display Name *</label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Your display name"
              required
            />
          </div>
          <div className="cp-field">
            <label>Headline</label>
            <input
              type="text"
              value={headline}
              onChange={e => setHeadline(e.target.value)}
              placeholder="e.g., Software Engineer seeking new opportunities"
            />
          </div>
          <div className="cp-field">
            <label>Bio</label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              placeholder="Tell the community about yourself..."
              rows={4}
            />
          </div>
          <div className="cp-field">
            <label>Location</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g., London, UK"
            />
          </div>
          <div className="cp-field">
            <label>Experience Level</label>
            <select
              value={experienceLevel}
              onChange={e => setExperienceLevel(e.target.value)}
            >
              <option value="">Select...</option>
              {EXPERIENCE_LEVELS.map(l => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Links */}
        <div className="cp-section">
          <h2>Links</h2>
          <div className="cp-field">
            <label>Avatar URL</label>
            <input
              type="url"
              value={avatarUrl}
              onChange={e => setAvatarUrl(e.target.value)}
              placeholder="https://example.com/avatar.jpg"
            />
          </div>
          <div className="cp-field">
            <label>LinkedIn</label>
            <input
              type="url"
              value={linkedInUrl}
              onChange={e => setLinkedInUrl(e.target.value)}
              placeholder="https://linkedin.com/in/..."
            />
          </div>
          <div className="cp-field">
            <label>Portfolio / Website</label>
            <input
              type="url"
              value={portfolioUrl}
              onChange={e => setPortfolioUrl(e.target.value)}
              placeholder="https://your-portfolio.com"
            />
          </div>
        </div>

        {/* Skills */}
        <div className="cp-section">
          <h2>Skills</h2>
          <div className="cp-skill-input">
            <input
              type="text"
              value={skillInput}
              onChange={e => setSkillInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
              placeholder="Type a skill and press Enter"
            />
            <button type="button" onClick={addSkill}>Add</button>
          </div>
          <div className="cp-tags">
            {skills.map(skill => (
              <span key={skill} className="cp-tag">
                {skill}
                <button type="button" onClick={() => removeSkill(skill)}>×</button>
              </span>
            ))}
            {skills.length === 0 && <span className="cp-hint">No skills added yet</span>}
          </div>
        </div>

        {/* Looking For */}
        <div className="cp-section">
          <h2>Looking For</h2>
          <div className="cp-chips">
            {LOOKING_FOR_OPTIONS.map(item => (
              <button
                key={item}
                type="button"
                className={`cp-chip ${lookingFor.includes(item) ? 'active' : ''}`}
                onClick={() => toggleLookingFor(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        {/* Industries */}
        <div className="cp-section">
          <h2>Industries</h2>
          <div className="cp-chips">
            {INDUSTRY_OPTIONS.map(item => (
              <button
                key={item}
                type="button"
                className={`cp-chip ${industries.includes(item) ? 'active' : ''}`}
                onClick={() => toggleIndustry(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        {/* Save */}
        <div className="cp-actions">
          <button type="submit" className="cp-save-btn" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          <button
            type="button"
            className="cp-view-btn"
            onClick={() => navigate('/community/members')}
          >
            Browse Members
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommunityProfile;
